import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Heart, ChevronRight, AlertTriangle, Check, Shield, Phone, Activity } from 'lucide-react';

const MOODS = [
  { key: 'happy', emoji: '😊', label: 'Happy', color: '#10b981', details: ['Celebrating something', 'Just feeling good', 'Had a win earlier'] },
  { key: 'neutral', emoji: '😐', label: 'Neutral', color: '#a0a8b8', details: ['Calm', 'No strong feelings', 'Just passing time'] },
  { key: 'stressed', emoji: '😟', label: 'Stressed', color: '#f59e0b', details: ['Work stress', 'Money worries', 'Personal issues'] },
  { key: 'angry', emoji: '😠', label: 'Angry', color: '#f97316', details: ['Frustrated with losses', 'Angry at something else', 'General irritation'] },
  { key: 'sad', emoji: '😢', label: 'Sad', color: '#ef4444', details: ['Feeling down', 'Lonely', 'Depressed'] },
];

export default function MoodTracker() {
  const [step, setStep] = useState('mood'); // mood, details, secondary, result
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodDetail, setMoodDetail] = useState('');
  const [alcohol, setAlcohol] = useState(false);
  const [recentLoss, setRecentLoss] = useState(false);
  const [alone, setAlone] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get('/tools/mood-checkins?limit=10').then(r => setHistory(r.data.checkins)).catch(() => {});
    api.get('/tools/mood-patterns').then(r => setPatterns(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    try {
      const res = await api.post('/tools/mood-checkin', { mood: selectedMood, mood_detail: moodDetail, alcohol, recent_loss: recentLoss, alone });
      setResult(res.data);
      setStep('result');
    } catch { toast.error('Failed to log mood'); }
  };

  const reset = () => { setStep('mood'); setSelectedMood(null); setMoodDetail(''); setAlcohol(false); setRecentLoss(false); setAlone(false); setResult(null); };

  if (step === 'result' && result) {
    const riskColor = result.risk_level === 'high' ? '#ef4444' : result.risk_level === 'moderate' ? '#f59e0b' : '#10b981';
    return (
      <div className="max-w-xl mx-auto px-4 pt-20 pb-12" data-testid="mood-result">
        <div className="ciq-card text-center animate-slide-up mb-6" style={{ borderColor: `${riskColor}30` }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${riskColor}15` }}>
            {result.risk_level === 'high' ? <AlertTriangle className="h-8 w-8" style={{ color: riskColor }} /> :
             result.risk_level === 'moderate' ? <Shield className="h-6 w-6" style={{ color: riskColor }} /> :
             <Check className="h-8 w-8" style={{ color: riskColor }} />}
          </div>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: riskColor }}>Risk Level: {result.risk_level}</p>
          <p className="text-3xl font-bold text-white mb-2">{result.risk_score}/100</p>
          <p className="text-sm text-[#a0a8b8] mb-4">{result.message}</p>
          {result.suggestions?.length > 0 && (
            <div className="text-left space-y-2 mb-4">
              <p className="text-xs text-[#a0a8b8] uppercase">Instead of gambling right now, try:</p>
              {result.suggestions.map((s, i) => <p key={i} className="text-sm text-white bg-[#1a1f2e] px-3 py-2 rounded-lg">{s}</p>)}
            </div>
          )}
          {result.risk_level === 'high' && (
            <div className="p-4 rounded-xl bg-[#ef4444]/5 border border-[#ef4444]/20 text-left mb-4">
              <p className="text-xs text-[#ef4444] font-medium mb-2">Support Resources</p>
              <p className="text-xs text-[#a0a8b8] flex items-center gap-2"><Phone className="h-3 w-3" /> National Gambling Helpline: 1-800-522-4700</p>
            </div>
          )}
          <button onClick={reset} className="h-10 px-6 rounded-xl bg-[#4d7cfe] text-white text-sm font-medium btn-lift">New Check-in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="mood-tracker-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Emotional Wellness</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Mood Tracker</span>
      </nav>
      <div className="flex items-start gap-4 mb-8">
        <div className="w-12 h-12 rounded-xl bg-[#ef4444]/15 flex items-center justify-center shrink-0"><Heart className="h-6 w-6 text-[#ef4444]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Mood-Before-Play Tracker</h1><p className="text-[#a0a8b8] text-sm mt-1">Pre-session emotional check-in with pattern analysis</p></div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0 max-w-xl">
          {step === 'mood' && (
            <div className="ciq-card animate-fade-in">
              <h2 className="text-xl font-bold text-white text-center mb-6">How are you feeling right now?</h2>
              <div className="grid grid-cols-5 gap-3">
                {MOODS.map(m => (
                  <button key={m.key} onClick={() => { setSelectedMood(m.key); setStep('details'); }}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#1a1f2e] border border-white/10 hover:border-white/20 transition-all" data-testid={`mood-${m.key}`}>
                    <span className="text-3xl">{m.emoji}</span>
                    <span className="text-xs text-[#a0a8b8]">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 'details' && selectedMood && (
            <div className="ciq-card animate-fade-in">
              <h3 className="text-white font-semibold mb-4">Can you be more specific?</h3>
              <div className="space-y-2 mb-6">
                {MOODS.find(m => m.key === selectedMood)?.details.map((d, i) => (
                  <button key={i} onClick={() => { setMoodDetail(d); setStep('secondary'); }}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${moodDetail === d ? 'bg-[#4d7cfe]/10 border-[#4d7cfe]/30 text-white' : 'bg-[#1a1f2e] border-white/10 text-[#a0a8b8] hover:border-white/20'}`}>{d}</button>
                ))}
              </div>
              <button onClick={() => setStep('secondary')} className="text-xs text-[#4d7cfe] hover:underline">Skip this step</button>
            </div>
          )}
          {step === 'secondary' && (
            <div className="ciq-card animate-fade-in">
              <h3 className="text-white font-semibold mb-4">Quick check</h3>
              <div className="space-y-4 mb-6">
                {[{ label: 'Have you been drinking?', value: alcohol, set: setAlcohol },
                  { label: 'Did you lose money recently?', value: recentLoss, set: setRecentLoss },
                  { label: 'Are you alone right now?', value: alone, set: setAlone }].map((q, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#a0a8b8]">{q.label}</span>
                    <div className="flex gap-2">
                      <button onClick={() => q.set(true)} className={`px-4 py-1.5 rounded-lg text-xs font-medium ${q.value ? 'bg-[#ef4444]/15 text-[#ef4444] border border-[#ef4444]/30' : 'bg-[#1a1f2e] text-[#64748b] border border-white/10'}`}>Yes</button>
                      <button onClick={() => q.set(false)} className={`px-4 py-1.5 rounded-lg text-xs font-medium ${!q.value ? 'bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30' : 'bg-[#1a1f2e] text-[#64748b] border border-white/10'}`}>No</button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={handleSubmit} className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift" data-testid="mood-submit-btn">Complete Check-in</button>
            </div>
          )}
        </div>

        {/* Pattern Sidebar */}
        <div className="w-full lg:w-[320px] shrink-0">
          {patterns && patterns.total_checkins > 0 && (
            <div className="ciq-card mb-4">
              <div className="flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-[#4d7cfe]" /><h3 className="text-white font-semibold text-sm">Your Patterns</h3></div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Check-ins</p><p className="text-lg font-bold text-white">{patterns.total_checkins}</p></div>
                <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Avg Risk</p><p className={`text-lg font-bold ${patterns.avg_risk > 50 ? 'text-[#ef4444]' : 'text-[#10b981]'}`}>{patterns.avg_risk}/100</p></div>
              </div>
              {patterns.patterns?.map((p, i) => <p key={i} className="text-xs text-[#a0a8b8] mb-2">- {p}</p>)}
            </div>
          )}
          {history.length > 0 && (
            <div className="ciq-card">
              <h3 className="text-white font-semibold text-sm mb-3">Recent Check-ins</h3>
              <div className="space-y-2">{history.slice(0, 5).map((c, i) => {
                const m = MOODS.find(m => m.key === c.mood);
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span>{m?.emoji}</span>
                    <span className="text-[#a0a8b8] flex-1">{new Date(c.timestamp).toLocaleDateString()}</span>
                    <span className={c.risk_score > 50 ? 'text-[#ef4444]' : 'text-[#10b981]'}>{c.risk_score}/100</span>
                  </div>
                );
              })}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
